import { Button, type ButtonProps } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import * as React from 'react';

interface PurpleGradientButtonProps extends ButtonProps {
  /**
   * Whether to apply the shimmer animation effect
   * @default false
   */
  withShimmer?: boolean | undefined;
  /**
   * Icon to display on the left side of the button
   */
  leftIcon?: React.ReactNode | undefined;
  /**
   * Icon to display on the right side of the button
   */
  rightIcon?: React.ReactNode | undefined;
}

const PurpleGradientButton = React.forwardRef<
  HTMLButtonElement,
  PurpleGradientButtonProps
>(
  (
    {
      className,
      children,
      withShimmer = false,
      leftIcon,
      rightIcon,
      disabled,
      ...props
    },
    ref
  ) => {
    return (
      <Button
        ref={ref}
        disabled={disabled}
        className={cn(
          // Base styles
          'flex items-center justify-center gap-2',
          '[background:var(--gradient-button)]',
          'hover:opacity-90',
          // #8795: was text-content-inverse — near-black (#141413) in the
          // default/calm/warm dark palettes against this gradient
          // (2.85-3.41:1, fails AA); text-cta-text resolves to solid
          // #ffffff in all 8 palette/mode combos and clears AA everywhere.
          'text-cta-text font-semibold',
          'transition-all duration-300',
          'rounded-lg',
          // Disabled styles
          'disabled:opacity-50 disabled:cursor-not-allowed',
          // Shimmer effect
          withShimmer && 'shimmer-button relative overflow-hidden',
          className
        )}
        {...props}
      >
        {leftIcon && <span className='flex-shrink-0'>{leftIcon}</span>}
        {children}
        {rightIcon && <span className='flex-shrink-0'>{rightIcon}</span>}
      </Button>
    );
  }
);

PurpleGradientButton.displayName = 'PurpleGradientButton';

export { PurpleGradientButton };
